import { useState, useEffect, useCallback, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { seekSupabase } from '@/integrations/supabase/seekClient';
import type { Json } from '@/integrations/supabase/types';
import { useSeekAuth } from '@/contexts/SeekAuthContext';

export type DecisionAction = 'accept' | 'reject' | 'keep_current';

export interface FieldDecision {
  action: DecisionAction;
  edited_value?: Json;
  confidence?: number;
}

export interface ItemDecision {
  index: number;
  stable_key: string;
  action: DecisionAction;
  edited?: Json;
}

export interface AdditionalSectionDecision {
  stable_id: string;
  action: DecisionAction;
}

export interface ReviewDecisions {
  headline?: FieldDecision;
  professional_summary?: FieldDecision;
  current_job_title?: FieldDecision;
  city?: FieldDecision;
  country?: FieldDecision;
  nationality?: FieldDecision;
  highest_education_level?: FieldDecision;
  total_years_experience?: FieldDecision;
  experiences?: ItemDecision[];
  education?: ItemDecision[];
  skills?: FieldDecision;
  languages?: FieldDecision;
  certifications?: FieldDecision;
  additional_sections?: AdditionalSectionDecision[];
}

const PROFILE_DECISION_FIELDS = [
  'headline', 'professional_summary', 'current_job_title',
  'city', 'country', 'nationality',
  'highest_education_level', 'total_years_experience',
  'skills', 'languages', 'certifications',
] as const;

type ProfileDecisionField = typeof PROFILE_DECISION_FIELDS[number];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function recordOrEmpty(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {};
}

function recordArray(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function stringValue(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function isProfileDecisionField(value: string): value is ProfileDecisionField {
  return PROFILE_DECISION_FIELDS.some((field) => field === value);
}

function toJson(value: unknown): Json {
  if (value === null) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') {
    return value;
  }
  if (Array.isArray(value)) return value.map(toJson);
  if (isRecord(value)) {
    const json: { [key: string]: Json } = {};
    for (const [key, item] of Object.entries(value)) {
      if (item !== undefined) json[key] = toJson(item);
    }
    return json;
  }
  return null;
}

/**
 * Confidence scoring: converts extraction confidence levels to numeric scores.
 * High ≥ 0.85 → pre-fill accept
 * Medium 0.50–0.84 → require review
 * Low < 0.50 → default keep_current
 */
function confidenceToNumber(level?: string): number {
  if (!level) return 0.7;
  switch (level) {
    case 'high': return 0.9;
    case 'medium': return 0.7;
    case 'low': return 0.3;
    default: return 0.7;
  }
}

function presetAction(conf: number): DecisionAction {
  return conf >= 0.85 ? 'accept' : 'keep_current';
}

/** Detect weak sections that need the most attention */
export interface WeakSection {
  section: string;
  label: string;
  reason: string;
  priority: 'high' | 'medium' | 'low';
}

export function detectWeakSections(parsedData: unknown, currentData: unknown): WeakSection[] {
  const weaknesses: WeakSection[] = [];
  const parsed = recordOrEmpty(parsedData);
  const currentProfile = recordOrEmpty(recordOrEmpty(currentData).profile);
  const experiences = recordArray(parsed.experiences);

  // Missing or very short summary
  const summary = stringValue(parsed.professional_summary) || stringValue(currentProfile.professional_summary);
  if (!summary || summary.length < 50) {
    weaknesses.push({ section: 'professional_summary', label: 'Professional Summary', reason: summary ? 'Too brief — aim for 2-3 impactful sentences' : 'Missing entirely', priority: 'high' });
  }

  // No experiences
  const expCount = experiences.length;
  if (expCount === 0) {
    weaknesses.push({ section: 'experiences', label: 'Work Experience', reason: 'No experience entries found', priority: 'high' });
  } else {
    // Experiences with no bullet points / description
    const emptyDescExps = experiences.filter((experience) => stringValue(experience.description).trim().length < 20);
    if (emptyDescExps.length > 0) {
      weaknesses.push({ section: 'experiences', label: 'Work Experience', reason: `${emptyDescExps.length} role(s) lack achievement descriptions`, priority: 'medium' });
    }
  }

  // Missing education
  if (recordArray(parsed.education).length === 0) {
    weaknesses.push({ section: 'education', label: 'Education', reason: 'No education entries found', priority: 'medium' });
  }

  // Very few skills
  const skillGroups = recordOrEmpty(parsed.skills);
  const skillCount = Array.isArray(parsed.skills) ? parsed.skills.length
    : (Array.isArray(skillGroups.skills) ? skillGroups.skills.length : 0)
      + (Array.isArray(skillGroups.expertise) ? skillGroups.expertise.length : 0)
      + (Array.isArray(skillGroups.key_strengths) ? skillGroups.key_strengths.length : 0);
  if (skillCount < 3) {
    weaknesses.push({ section: 'skills', label: 'Skills', reason: skillCount === 0 ? 'No skills listed' : 'Too few skills — aim for 5-10 relevant ones', priority: 'medium' });
  }

  // No headline
  if (!stringValue(parsed.headline) && !stringValue(currentProfile.headline)) {
    weaknesses.push({ section: 'headline', label: 'Headline', reason: 'Missing — a strong headline improves discoverability', priority: 'low' });
  }

  return weaknesses.sort((a, b) => {
    const ord = { high: 0, medium: 1, low: 2 };
    return ord[a.priority] - ord[b.priority];
  });
}

export function buildInitialDecisions(
  parsedData: unknown,
  currentData: unknown,
  confidence: Record<string, string>,
): ReviewDecisions {
  const decisions: ReviewDecisions = {};
  const parsed = recordOrEmpty(parsedData);
  const currentProfile = recordOrEmpty(recordOrEmpty(currentData).profile);

  const profileFields = [
    'headline', 'professional_summary', 'current_job_title',
    'city', 'country', 'nationality',
    'highest_education_level', 'total_years_experience',
  ] as const;

  for (const field of profileFields) {
    const cvVal = parsed[field];
    const curVal = currentProfile[field];
    if (cvVal != null && cvVal !== '') {
      const conf = confidenceToNumber(confidence?.personal_info);
      const hasCurrent = curVal != null && curVal !== '';
      decisions[field] = {
        action: hasCurrent ? presetAction(conf) : 'accept',
        edited_value: toJson(cvVal),
        confidence: conf,
      };
    }
  }

  // Experiences — stable key from title + employer
  const experiences = recordArray(parsed.experiences);
  if (experiences.length > 0) {
    const conf = confidenceToNumber(confidence?.experiences);
    decisions.experiences = experiences.map((experience, i) => ({
      index: i,
      stable_key: `exp_${stringValue(experience.title).toLowerCase().replace(/\s+/g, '_')}_${stringValue(experience.employer_name).toLowerCase().replace(/\s+/g, '_')}`,
      action: presetAction(conf),
      edited: toJson(experience),
    }));
  }

  // Education — stable key from institution + degree
  const education = recordArray(parsed.education);
  if (education.length > 0) {
    const conf = confidenceToNumber(confidence?.education);
    decisions.education = education.map((educationItem, i) => ({
      index: i,
      stable_key: `edu_${stringValue(educationItem.institution_name).toLowerCase().replace(/\s+/g, '_')}_${stringValue(educationItem.degree).toLowerCase().replace(/\s+/g, '_')}`,
      action: presetAction(conf),
      edited: toJson(educationItem),
    }));
  }

  // Skills (bulk decision)
  const skillGroups = recordOrEmpty(parsed.skills);
  const allSkillCount = Array.isArray(parsed.skills)
    ? parsed.skills.length
    : (Array.isArray(skillGroups.skills) ? skillGroups.skills.length : 0)
      + (Array.isArray(skillGroups.expertise) ? skillGroups.expertise.length : 0)
      + (Array.isArray(skillGroups.key_strengths) ? skillGroups.key_strengths.length : 0);
  if (allSkillCount > 0) {
    const conf = confidenceToNumber(confidence?.skills);
    decisions.skills = { action: presetAction(conf), confidence: conf };
  }

  // Languages
  if (Array.isArray(parsed.languages) && parsed.languages.length > 0) {
    const conf = confidenceToNumber(confidence?.languages);
    decisions.languages = { action: presetAction(conf), confidence: conf };
  }

  // Certifications
  if (Array.isArray(parsed.certifications) && parsed.certifications.length > 0) {
    const conf = confidenceToNumber(confidence?.certifications);
    decisions.certifications = { action: presetAction(conf), confidence: conf };
  }

  // Additional sections
  const additionalSections = recordArray(parsed.additional_sections);
  if (additionalSections.length > 0) {
    decisions.additional_sections = additionalSections
      .filter((section) => section.quality !== 'junk' && typeof section.stable_id === 'string')
      .map((section) => ({
        stable_id: stringValue(section.stable_id),
        action: (typeof section.confidence === 'number' ? section.confidence : 0.7) >= 0.85
          ? 'accept' as DecisionAction
          : 'keep_current' as DecisionAction,
      }));
  }

  return decisions;
}

/** Count decisions by action type for summary display */
export function summarizeDecisions(decisions: ReviewDecisions): { accepted: number; rejected: number; kept: number; total: number } {
  let accepted = 0, rejected = 0, kept = 0;

  for (const f of PROFILE_DECISION_FIELDS) {
    const d = decisions[f];
    if (!d) continue;
    if (d.action === 'accept') accepted++;
    else if (d.action === 'reject') rejected++;
    else kept++;
  }

  for (const items of [decisions.experiences, decisions.education]) {
    if (!items) continue;
    for (const item of items) {
      if (item.action === 'accept') accepted++;
      else if (item.action === 'reject') rejected++;
      else kept++;
    }
  }

  for (const item of decisions.additional_sections || []) {
    if (item.action === 'accept') accepted++;
    else if (item.action === 'reject') rejected++;
    else kept++;
  }

  return { accepted, rejected, kept, total: accepted + rejected + kept };
}

export function useReviewSession() {
  const { user } = useSeekAuth();
  const uid = user?.id;
  const qc = useQueryClient();
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [decisions, setDecisions] = useState<ReviewDecisions>({});
  const [importId, setImportId] = useState<string | null>(null);

  // Check for active (in-progress) review session on mount
  const activeSessionQuery = useQuery({
    queryKey: ['review-session-active', uid],
    queryFn: async () => {
      const { data, error } = await seekSupabase
        .from('candidate_review_sessions')
        .select('*')
        .eq('candidate_id', uid!)
        .eq('status', 'in_progress')
        .order('created_at', { ascending: false })
        .limit(1);
      if (error) throw error;
      return data?.[0] ?? null;
    },
    enabled: !!uid,
    staleTime: 30_000,
  });

  // Restore session on query load
  useEffect(() => {
    const session = activeSessionQuery.data;
    if (session && !sessionId) {
      setSessionId(session.id);
      setImportId(session.import_id);
      try {
        const restored = typeof session.decisions_json === 'string'
          ? JSON.parse(session.decisions_json)
          : session.decisions_json;
        if (restored && typeof restored === 'object') {
          setDecisions(restored);
        }
      } catch {
        // corrupt decisions, start fresh
      }
    }
  }, [activeSessionQuery.data, sessionId]);

  // Debounced save
  const persistDecisions = useCallback(async (sid: string, dec: ReviewDecisions) => {
    if (!uid) return;
    await seekSupabase
      .from('candidate_review_sessions')
      .update({ decisions_json: toJson(dec), updated_at: new Date().toISOString() })
      .eq('id', sid);
  }, [uid]);

  const scheduleSave = useCallback((dec: ReviewDecisions) => {
    if (!sessionId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      persistDecisions(sessionId, dec);
    }, 1500);
  }, [sessionId, persistDecisions]);

  const updateDecision = useCallback(<K extends keyof ReviewDecisions>(
    key: K,
    value: ReviewDecisions[K]
  ) => {
    setDecisions(prev => {
      const next = { ...prev, [key]: value };
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  const updateFieldAction = useCallback((field: string, action: DecisionAction) => {
    setDecisions(prev => {
      if (!isProfileDecisionField(field)) return prev;
      const existing = prev[field];
      if (!existing) return prev;
      const next = { ...prev, [field]: { ...existing, action } };
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  const updateItemAction = useCallback((
    section: 'experiences' | 'education',
    index: number,
    action: DecisionAction
  ) => {
    setDecisions(prev => {
      const items = [...(prev[section] || [])];
      const idx = items.findIndex(i => i.index === index);
      if (idx >= 0) items[idx] = { ...items[idx], action };
      const next = { ...prev, [section]: items };
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  const updateAdditionalAction = useCallback((stableId: string, action: DecisionAction) => {
    setDecisions(prev => {
      const items = [...(prev.additional_sections || [])];
      const idx = items.findIndex(i => i.stable_id === stableId);
      if (idx >= 0) items[idx] = { ...items[idx], action };
      const next = { ...prev, additional_sections: items };
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  // Start a new review session
  const startSession = useMutation({
    mutationFn: async ({ importId: iid, initialDecisions }: { importId: string; initialDecisions: ReviewDecisions }) => {
      if (!uid) throw new Error('Not authenticated');

      // Expire any old in_progress sessions
      await seekSupabase
        .from('candidate_review_sessions')
        .update({ status: 'expired', updated_at: new Date().toISOString() })
        .eq('candidate_id', uid)
        .eq('status', 'in_progress');

      const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();

      const { data, error } = await seekSupabase
        .from('candidate_review_sessions')
        .insert({
          candidate_id: uid,
          import_id: iid,
          status: 'in_progress',
          decisions_json: toJson(initialDecisions),
          expires_at: expiresAt,
        })
        .select('id')
        .single();

      if (error) throw error;
      return { id: data.id, importId: iid, decisions: initialDecisions };
    },
    onSuccess: (result) => {
      setSessionId(result.id);
      setImportId(result.importId);
      setDecisions(result.decisions);
      qc.invalidateQueries({ queryKey: ['review-session-active', uid] });
    },
  });

  // Complete (apply) the session
  const completeSession = useMutation({
    mutationFn: async () => {
      if (!sessionId || !uid) throw new Error('No active session');
      await persistDecisions(sessionId, decisions);
      await seekSupabase
        .from('candidate_review_sessions')
        .update({
          status: 'applied',
          applied_at: new Date().toISOString(),
          decisions_json: toJson(decisions),
          updated_at: new Date().toISOString(),
        })
        .eq('id', sessionId);
    },
    onSuccess: () => {
      setSessionId(null);
      setDecisions({});
      setImportId(null);
      qc.invalidateQueries({ queryKey: ['review-session-active', uid] });
    },
  });

  // Discard the session
  const discardSession = useMutation({
    mutationFn: async () => {
      if (!sessionId || !uid) throw new Error('No active session');
      await seekSupabase
        .from('candidate_review_sessions')
        .update({ status: 'discarded', updated_at: new Date().toISOString() })
        .eq('id', sessionId);
    },
    onSuccess: () => {
      setSessionId(null);
      setDecisions({});
      setImportId(null);
      qc.invalidateQueries({ queryKey: ['review-session-active', uid] });
    },
  });

  // ── ROLLBACK: Restore profile from pre-sync snapshot ──
  const rollbackSync = useMutation({
    mutationFn: async (targetImportId: string) => {
      if (!uid) throw new Error('Not authenticated');
      const res = await seekSupabase.functions.invoke('sync-cv-to-profile', {
        body: { import_id: targetImportId, rollback: true },
      });
      if (res.error) {
        const msg = typeof res.error === 'object' && 'message' in res.error
          ? res.error.message : String(res.error);
        throw new Error(msg || 'Rollback failed');
      }
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['candidate-profile', uid] });
      qc.invalidateQueries({ queryKey: ['candidate-experiences', uid] });
      qc.invalidateQueries({ queryKey: ['candidate-education', uid] });
      qc.invalidateQueries({ queryKey: ['candidate-skills', uid] });
      qc.invalidateQueries({ queryKey: ['candidate-languages', uid] });
      qc.invalidateQueries({ queryKey: ['candidate-certifications', uid] });
      qc.invalidateQueries({ queryKey: ['candidate-links', uid] });
      qc.invalidateQueries({ queryKey: ['profile-completion'] });
    },
  });

  // Cleanup timer
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const hasActiveSession = !!sessionId;
  const isRecoveredSession = !!activeSessionQuery.data && sessionId === activeSessionQuery.data?.id;

  return {
    sessionId,
    importId,
    decisions,
    hasActiveSession,
    isRecoveredSession,
    isLoadingSession: activeSessionQuery.isLoading,
    startSession,
    completeSession,
    discardSession,
    rollbackSync,
    updateDecision,
    updateFieldAction,
    updateItemAction,
    updateAdditionalAction,
    setDecisions,
  };
}
